import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChartLineUp,
  Trophy,
  Target,
  Flag,
  Crown,
  Sparkle,
  Trash,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import HandicapMilestoneCard from "../components/HandicapMilestoneCard";
import ShotDispersion from "../components/ShotDispersion";
import MyYardageChartCard from "../components/MyYardageChartCard";

export default function Stats() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [h2h, setH2h] = useState([]);
  const [dispersion, setDispersion] = useState([]);
  // Manual entries from the Club Bag screen — merged into the printable
  // yardage chart so users with no logged shots yet still get a useful card.
  const [clubYardages, setClubYardages] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const [s, h, d, b] = await Promise.all([
      api.get("/stats/me"),
      api.get("/stats/head-to-head"),
      api.get("/stats/dispersion"),
      // Manual bag yardages — best-effort, used to fill in clubs the user
      // hasn't logged shots for yet. Silent fallback so loss of this call
      // never breaks the Stats page.
      api.get("/auth/me/clubs").catch(() => ({ data: {} })),
    ]);
    setStats(s.data);
    setH2h(h.data?.opponents || []);
    setDispersion(d.data?.clubs || []);
    setClubYardages(b.data?.yardages || {});
  };

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const deleteRound = async (r) => {
    if (!window.confirm(`Delete your round at ${r.course_name}? It'll also disappear from any league standings.`)) return;
    try {
      await api.delete(`/rounds/${r.round_id}`);
      await reload();
    } catch {
      window.alert("Couldn't delete that round. Try again.");
    }
  };

  const deleteAllRounds = async () => {
    if (!window.confirm("Delete ALL your rounds? This wipes your stats + league standings. Can't be undone.")) return;
    try {
      const { data } = await api.delete("/rounds");
      window.alert(`Deleted ${data?.deleted ?? 0} round${(data?.deleted ?? 0) === 1 ? "" : "s"}.`);
      await reload();
    } catch {
      window.alert("Couldn't delete rounds. Try again.");
    }
  };

  if (loading) return <div className="px-5 pt-6 text-[var(--text-secondary)]">Loading stats…</div>;
  if (!stats) return <div className="px-5 pt-6 text-[var(--text-secondary)]">Could not load stats.</div>;

  const distTotal = Math.max(
    1,
    stats.shot_distribution.eagles +
      stats.shot_distribution.birdies +
      stats.shot_distribution.pars +
      stats.shot_distribution.bogeys +
      stats.shot_distribution.doubles_plus
  );
  const dist = [
    { key: "eagles", label: "Eagle+", count: stats.shot_distribution.eagles, color: "#7c3aed" },
    { key: "birdies", label: "Birdie", count: stats.shot_distribution.birdies, color: "var(--status-good)" },
    { key: "pars", label: "Par", count: stats.shot_distribution.pars, color: "var(--primary)" },
    { key: "bogeys", label: "Bogey", count: stats.shot_distribution.bogeys, color: "var(--status-warn)" },
    { key: "doubles_plus", label: "Double+", count: stats.shot_distribution.doubles_plus, color: "var(--status-bad)" },
  ];

  return (
    <div className="px-5 pt-6 pb-8">
      <Link to="/app" className="btn-ghost -ml-2 mb-3" data-testid="stats-back-link">
        <ArrowLeft size={20} weight="bold" />
      </Link>

      <p className="label-uppercase">Performance</p>
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-5">Your stats</h1>

      {stats.rounds_played === 0 ? (
        <>
          <div className="surface-card-flat p-6 text-center" data-testid="stats-empty">
            <ChartLineUp size={48} weight="duotone" className="mx-auto text-[var(--text-secondary)] mb-2" />
            <p className="text-[var(--text-secondary)]">No completed rounds yet. Finish a round to unlock your stats.</p>
          </div>
          {dispersion.length > 0 && (
            <div className="mt-6">
              <ShotDispersion clubs={dispersion} />
            </div>
          )}
        </>
      ) : (
        <>
          <HandicapMilestoneCard
            milestone={stats.milestone}
            userName={user?.name}
            onDismiss={() => setStats((s) => ({ ...s, milestone: null }))}
          />

          <HandicapHero
            index={stats.handicap_index}
            roundsUsed={stats.handicap_rounds_used}
            totalRounds={stats.rounds_played}
          />

          <TrendChart recent={stats.recent} />

          <div className="grid grid-cols-2 gap-3 mb-4">
            <Stat label="Rounds" value={stats.rounds_played} testid="stat-rounds-played" />
            <Stat label="Avg strokes" value={stats.avg_strokes ?? "—"} testid="stat-avg-strokes" />
            <Stat label="Avg vs par" value={fmtSigned(stats.avg_vs_par)} testid="stat-avg-vs-par" />
            <Stat label="Best round" value={`${stats.best_strokes ?? "—"} (${fmtSigned(stats.best_vs_par)})`} testid="stat-best-round" />
          </div>

          {stats.avg_putts_per_hole !== null && (
            <div className="surface-card-flat p-4 mb-4" data-testid="putts-stat">
              <p className="label-uppercase">Avg putts per hole</p>
              <div className="font-mono-num font-black text-3xl text-[var(--primary)] mt-1">{stats.avg_putts_per_hole}</div>
            </div>
          )}

          <ParBreakdown breakdown={stats.par_breakdown} />

          <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3">Shot distribution</h3>
          <div className="surface-card-flat p-4 mb-5" data-testid="shot-distribution">
            <div className="flex h-3 rounded-full overflow-hidden mb-3">
              {dist.map((d) => (
                <motion.div
                  key={d.key}
                  initial={{ width: 0 }}
                  animate={{ width: `${(d.count / distTotal) * 100}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  style={{ background: d.color }}
                />
              ))}
            </div>
            <div className="grid grid-cols-5 gap-1">
              {dist.map((d) => (
                <div key={d.key} className="text-center">
                  <div className="font-mono-num font-bold text-[var(--primary)]">{d.count}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">{d.label}</div>
                </div>
              ))}
            </div>
          </div>

          {stats.course_bests?.length > 0 && (
            <CourseBests bests={stats.course_bests} />
          )}

          {/* Bag IQ — explain what dispersion is + why it matters. Shown even
              with 0 shots so users know the feature exists. */}
          <div className="mt-6 mb-3" data-testid="bagiq-header">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-lg text-[var(--primary)]">Bag IQ</h3>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                {dispersion.length > 0
                  ? `${dispersion.length} club${dispersion.length === 1 ? "" : "s"} learned`
                  : "Tap clubs after shots to learn"}
              </span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] leading-snug">
              Your real club distances, learned automatically from the shots you log in-round.
              After 5+ shots per club, the Smart Caddie uses <em>your</em> numbers — not bag averages.
            </p>
          </div>

          {/* Shareable / printable yardage chart — pulls from dispersion +
              manual bag values. Hidden entirely when the user has no data. */}
          <MyYardageChartCard
            dispersion={dispersion}
            clubYardages={clubYardages}
            userName={user?.name}
          />

          <ShotDispersion clubs={dispersion} />

          {h2h.length > 0 && <HeadToHead opponents={h2h} />}

          {stats.recent.length > 0 && (
            <>
              <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3">Recent rounds</h3>
              <div className="space-y-2 mb-3" data-testid="recent-rounds-list">
                {[...stats.recent].slice(0, 10).map((r, i) => (
                  <motion.div
                    key={r.round_id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="surface-card-flat p-3 flex items-center justify-between gap-2"
                  >
                    <Link to={`/app/rounds/${r.round_id}/summary`} className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-[var(--primary)] text-sm line-clamp-1">{r.course_name}</div>
                      <div className="text-xs text-[var(--text-secondary)]">{new Date(r.started_at).toLocaleDateString("en-GB", { day:"numeric", month:"short" })}</div>
                    </Link>
                    <div className="text-right">
                      <div className="font-mono-num font-bold text-[var(--primary)]">{r.total_strokes}</div>
                      <div className={`text-xs font-bold ${vsParClass(r.vs_par)}`}>{fmtSigned(r.vs_par)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteRound(r)}
                      data-testid={`delete-round-${r.round_id}`}
                      aria-label="Delete round"
                      className="h-8 w-8 flex items-center justify-center rounded-full text-[var(--text-secondary)] hover:bg-red-500/10 hover:text-red-600 transition-colors"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </motion.div>
                ))}
              </div>
              <button
                type="button"
                onClick={deleteAllRounds}
                data-testid="delete-all-rounds"
                className="text-xs font-bold uppercase tracking-wider text-red-600 hover:underline mt-1"
              >
                Delete all rounds
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function HandicapHero({ index, roundsUsed, totalRounds }) {
  if (index === null || index === undefined) {
    return (
      <div
        className="surface-card-flat p-5 mb-4 relative overflow-hidden"
        data-testid="handicap-hero-empty"
      >
        <div className="flex items-start gap-3">
          <Target size={32} weight="duotone" className="text-[var(--text-secondary)] flex-shrink-0 mt-1" />
          <div className="flex-1">
            <p className="label-uppercase">Handicap index</p>
            <div className="font-mono-num font-black text-3xl text-[var(--text-secondary)] mt-1">—</div>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              Play {Math.max(3 - totalRounds, 0)} more {totalRounds === 2 ? "round" : "rounds"} to unlock your handicap.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const lower = index <= 18;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl p-5 mb-4 text-[#F2EEE7]"
      style={{
        background: "linear-gradient(135deg, #143D2F 0%, #1f5a45 60%, #c4543c 200%)",
      }}
      data-testid="handicap-hero"
    >
      <div
        style={{
          position: "absolute",
          right: -40,
          top: -40,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "rgba(196,84,60,0.22)",
          filter: "blur(2px)",
        }}
      />
      <div className="relative flex items-start gap-3">
        <Sparkle size={28} weight="fill" className="text-[#F2EEE7] flex-shrink-0 mt-1" style={{ opacity: 0.9 }} />
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-75">Handicap index</p>
          <div className="font-mono-num font-black text-5xl tracking-tight mt-1" data-testid="handicap-value">
            {index.toFixed(1)}
          </div>
          <p className="text-xs opacity-75 mt-1">
            From best {bestNFor(roundsUsed)} of your last {roundsUsed} {roundsUsed === 1 ? "round" : "rounds"}
            {lower ? " · Solid form 🔥" : ""}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function bestNFor(n) {
  const TABLE = { 3:1, 4:1, 5:1, 6:2, 7:2, 8:2, 9:3, 10:3, 11:3, 12:4, 13:4, 14:4, 15:5, 16:5, 17:6, 18:6, 19:7 };
  if (n >= 20) return 8;
  return TABLE[n] || 1;
}

function TrendChart({ recent }) {
  if (!recent || recent.length < 2) return null;
  // Recent is newest-first; reverse for chronological x-axis
  const points = [...recent].reverse();
  const values = points.map((p) => p.vs_par);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const range = Math.max(1, max - min);
  const w = 320;
  const h = 100;
  const pad = 8;
  const step = (w - pad * 2) / Math.max(1, points.length - 1);
  const y = (v) => pad + ((max - v) / range) * (h - pad * 2);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${pad + i * step} ${y(p.vs_par)}`)
    .join(" ");
  const zeroY = y(0);

  return (
    <div className="surface-card-flat p-4 mb-4" data-testid="score-trend">
      <div className="flex items-center justify-between mb-2">
        <p className="label-uppercase">Score trend</p>
        <span className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">vs par · last {points.length}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none">
        <line
          x1={pad}
          x2={w - pad}
          y1={zeroY}
          y2={zeroY}
          stroke="var(--text-secondary)"
          strokeOpacity="0.25"
          strokeDasharray="3 4"
        />
        <motion.path
          d={path}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
        {points.map((p, i) => (
          <circle
            key={p.round_id}
            cx={pad + i * step}
            cy={y(p.vs_par)}
            r={i === points.length - 1 ? 4 : 2.5}
            fill={p.vs_par <= 0 ? "var(--status-good)" : "var(--accent, #C4543C)"}
          />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-[var(--text-secondary)] mt-1 font-mono-num">
        <span>{fmtSigned(points[0].vs_par)}</span>
        <span>{fmtSigned(points[points.length - 1].vs_par)} ← latest</span>
      </div>
    </div>
  );
}

function ParBreakdown({ breakdown }) {
  if (!breakdown) return null;
  const has = [3, 4, 5].some((p) => breakdown[`par${p}`]);
  if (!has) return null;
  return (
    <>
      <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3">By hole type</h3>
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="par-breakdown">
        {[3, 4, 5].map((p) => {
          const data = breakdown[`par${p}`];
          if (!data) {
            return (
              <div key={p} className="surface-card-flat p-3 text-center opacity-60">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-bold">Par {p}</div>
                <div className="font-mono-num font-black text-xl text-[var(--text-secondary)] mt-1">—</div>
              </div>
            );
          }
          return (
            <div key={p} className="surface-card-flat p-3 text-center" data-testid={`par${p}-card`}>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-bold">Par {p}</div>
              <div className="font-mono-num font-black text-2xl text-[var(--primary)] mt-1">{data.avg_strokes}</div>
              <div className={`text-xs font-bold ${vsParClass(data.avg_vs_par)}`}>{fmtSigned(data.avg_vs_par)}</div>
              <div className="text-[10px] text-[var(--text-secondary)] mt-1">{data.holes_played} holes</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function CourseBests({ bests }) {
  return (
    <>
      <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3 flex items-center gap-2">
        <Flag size={18} weight="fill" /> Course personal bests
      </h3>
      <div className="space-y-2 mb-5" data-testid="course-bests">
        {bests.map((c, i) => (
          <motion.div
            key={c.course_id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="surface-card-flat p-3 flex items-center justify-between"
          >
            <div className="min-w-0 flex-1 pr-3">
              <div className="font-heading font-semibold text-[var(--primary)] text-sm line-clamp-1">
                {c.course_name}
              </div>
              <div className="text-xs text-[var(--text-secondary)]">
                {c.rounds_played} {c.rounds_played === 1 ? "round" : "rounds"}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono-num font-bold text-[var(--primary)]">{c.best_strokes}</div>
              <div className={`text-xs font-bold ${vsParClass(c.best_vs_par)}`}>{fmtSigned(c.best_vs_par)}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </>
  );
}

function HeadToHead({ opponents }) {
  return (
    <>
      <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3 flex items-center gap-2">
        <Trophy size={18} weight="fill" /> Head-to-head
      </h3>
      <div className="space-y-2 mb-5" data-testid="head-to-head">
        {opponents.map((o, i) => {
          const total = o.wins + o.losses + o.ties;
          const ahead = o.wins > o.losses;
          const winPct = total ? Math.round((o.wins / total) * 100) : 0;
          return (
            <motion.div
              key={o.opponent_id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="surface-card-flat p-3"
              data-testid={`h2h-${o.opponent_id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {ahead && <Crown size={16} weight="fill" className="text-[var(--accent, #C4543C)] flex-shrink-0" />}
                  <div className="font-heading font-semibold text-[var(--primary)] text-sm line-clamp-1">
                    vs {o.opponent_name}
                  </div>
                </div>
                <div className="text-xs font-bold font-mono-num text-[var(--primary)]">
                  {o.wins}W · {o.losses}L · {o.ties}T
                </div>
              </div>
              <div className="flex h-2 rounded-full overflow-hidden bg-[var(--text-secondary)]/10">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${total ? (o.wins / total) * 100 : 0}%` }}
                  transition={{ duration: 0.6 }}
                  className="h-full"
                  style={{ background: "var(--status-good)" }}
                />
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${total ? (o.ties / total) * 100 : 0}%` }}
                  transition={{ duration: 0.6 }}
                  className="h-full"
                  style={{ background: "var(--text-secondary)" }}
                />
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${total ? (o.losses / total) * 100 : 0}%` }}
                  transition={{ duration: 0.6 }}
                  className="h-full"
                  style={{ background: "var(--status-bad)" }}
                />
              </div>
              <div className="text-[10px] text-[var(--text-secondary)] mt-1">
                {winPct}% win rate · {o.leagues_shared.length}{" "}
                {o.leagues_shared.length === 1 ? "shared league" : "shared leagues"}
              </div>
            </motion.div>
          );
        })}
      </div>
    </>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="surface-card-flat p-4" data-testid={testid}>
      <p className="label-uppercase">{label}</p>
      <div className="font-mono-num font-black text-2xl text-[var(--primary)] mt-1">{value}</div>
    </div>
  );
}

function fmtSigned(v) {
  if (v === null || v === undefined) return "—";
  if (v === 0) return "E";
  return v > 0 ? `+${v}` : String(v);
}
function vsParClass(v) {
  if (v < 0) return "score-birdie";
  if (v === 0) return "score-par";
  if (v <= 1) return "score-bogey";
  return "score-double";
}
